import { Fingerprint } from 'app/pages/frames/helpers/fingerprint';
import { captureOrderService } from '../http/orderService';
import { use3DSFlow } from '../lib/3DS';
import { getDifferenceInSeconds } from 'app/util/dates';
import get from 'lodash.get';
import { useEffect, useState } from 'react';
import { useErrorStore } from 'app/context/errorStore';
import { useCheckoutFrameStore } from 'app/context/checkoutStore';
import { useTranslation } from 'react-i18next';
import { Order } from '../util/interface';
import { transformCapturedOrderDtoToOrderModel } from 'app/factories/orderFactory';

interface Props {
  paymentRequestData: any;
  initialTime: Date;
  orderPaymentSourceData?: any;
  binNumber?: string;
  formCard?: Record<string, any>;
}

export const useCreditCardSubtmitOrder = () => {
  const { setError, setIsLoading } = useErrorStore();
  const { checkoutRequest, entity } = useCheckoutFrameStore();
  const [orderWithPaymentSource, setOrderWithPaymentSource] = useState<any>(null);
  const { dispose, execute: execute3DSFlow } = use3DSFlow();
  const { t } = useTranslation();

  useEffect(() => {
    if (entity) {
      validate3DS();
    }
    return () => dispose();
  }, [entity]);

  async function doPostWrapper(props: Props) {
    setIsLoading(true);
    await doPost(props);
  }

  function validate3DS() {
    const { threeDs } = entity!;
    if (threeDs === '') {
      throw Error(t('errors.internal'));
    }
  }

  async function captureOrder(dataRequest: { fingerprint?: string; data: Record<string, any> }) {
    try {
      const orderResponse = await captureOrderService(dataRequest);
      if (orderResponse.paymentStatus === 'error') {
        const errorMessage = get(orderResponse.errors, '[0].message', '');
        setError(Error(errorMessage));
        return;
      }
      return orderResponse;
    } catch (e: any) {
      throw e;
    }
  }

  const startProcess = async ({ orderBody }: Record<string, any>) => {
    if (!checkoutRequest) {
      throw { source: 'checkoutRequest is undefined at startProcess', data: null };
    }
    let payload = orderBody;
    const {
      orderTemplate: { customerInfo = {} },
    } = checkoutRequest;

    try {
      const { requireChallenge, threeDSResult, ...additionalOrderData } = await execute3DSFlow(customerInfo);
      payload = { ...payload, ...additionalOrderData };
      const capturedOrder = await captureOrder({
        fingerprint: orderBody.fingerprint,
        data: payload,
      });
      return { ...capturedOrder, requireChallenge, threeDSResult };
    } catch (error: any) {
      throw error;
    }
  };

  async function doPost({ paymentRequestData, initialTime, orderPaymentSourceData }: Props) {
    try {
      if (!checkoutRequest) {
        throw { source: 'checkoutRequest is undefined at startProcess', data: null };
      }
      const fingerprint = await Fingerprint.getVisitorId();
      let orderPaymentSourceRes: any, capturedOrder: Order;
      const {
        orderTemplate: { customerInfo = {} },
        orders,
        liveMode,
      } = checkoutRequest;
      const { apiOrderId } = orders[0];
      paymentRequestData.fingerprint = fingerprint;
      if (orderPaymentSourceData) {
        orderPaymentSourceData.fingerprint = fingerprint;
        orderPaymentSourceRes = orderPaymentSourceData;
      }

      const timeTookToFillPaymentForm = getDifferenceInSeconds(new Date(), initialTime);

      const { paymentKey, paymentSourceId = '' } = orderPaymentSourceRes || {};
      if (!customerInfo) {
        setError(Error(t('errors.order.emptyCustomerId')));
        return;
      }
      const orderBody = {
        ...paymentRequestData,
        customerInfo: {
          ...customerInfo,
        },
        fillPaymentFormTime: timeTookToFillPaymentForm,
        fingerprint: fingerprint,
        paymentSourceId,
        apiOrderId,
        liveMode,
        ...(paymentKey ? { paymentKey } : {}),
      };
      orderBody.checkoutAntifraudResponseID = '6019a9a3b7030407c8def5c0';
      capturedOrder = await startProcess({
        orderBody,
        fingerprint,
      });
      setOrderWithPaymentSource((prev: any) => ({
        ...prev,
        order: transformCapturedOrderDtoToOrderModel(capturedOrder),
        paymentSource: orderPaymentSourceRes,
      }));
    } catch (e: any) {
      setError(e);
    }
  }

  return {
    orderWithPaymentSource,
    doPost: doPostWrapper,
  };
};
